"use client"

import type React from "react"
import { useState, useEffect } from "react"

interface JobDescriptionFieldProps {
  register: any
  errors: any
  watch?: any
  setValue?: any
  setNewPosition: (open: boolean) => void
  noJobFound: boolean
}

export default function JobDescriptionField({ register, errors, watch, noJobFound, setValue, setNewPosition }: JobDescriptionFieldProps) {
  const [charCount, setCharCount] = useState(0);
  const maxLength = 3000;

  // Watch the job description field value
  const jobDescriptionValue = watch ? watch("JobDescription") : ""

  useEffect(() => {
    if (jobDescriptionValue) {
      setCharCount(jobDescriptionValue.length)
    } else {
      setCharCount(0)
    }
  }, [jobDescriptionValue])

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value

    // Prevent input beyond max length
    if (value.length <= maxLength) {
      setCharCount(value.length)
      // Update the form value if setValue is provided
      if (setValue) {
        setValue("JobDescription", value)
      }
    } else {
      // Truncate to max length
      const truncatedValue = value.slice(0, maxLength)
      e.target.value = truncatedValue
      setCharCount(maxLength)
      if (setValue) {
        setValue("JobDescription", truncatedValue)
      }
    }
  }

  const getCounterColor = () => {
    const percentage = (charCount / maxLength) * 100
    if (percentage >= 95) return "text-red-500"
    if (percentage >= 80) return "text-orange-500"
    if (percentage >= 60) return "text-yellow-600"
    return "text-slate-500 dark:text-slate-400"
  }

  const remainingChars = maxLength - charCount

  return (
    <div className="flex flex-col py-4   h-[86%] gap-2">
      <div className="flex justify-between items-center mb-1">
        <p className="text-xs font-semibold text-slate-700 dark:text-slate-300">Job Description</p>
        {!noJobFound && <button className="flex items-center gap-1 bg-accent-400 hover:bg-accent-500 text-white font-medium px-4 py-2 rounded-lg transition"
          onClick={() => { setNewPosition(false); }} >
          Positions list
        </button>}
      </div>

      <textarea
        {...register("JobDescription", {
          required: "Job description is required",
          maxLength: {
            value: maxLength,
            message: `Job description must be less than ${maxLength} characters`,
          },
        })}
        onChange={handleInputChange}
        maxLength={maxLength}
        className={`w-full p-4 text-xs  min-h-[200px] md:min-h-full flex-1 rounded-lg ${errors.JobDescription?.message && "border border-red-500"
          } text-slate-700 dark:text-slate-300  border border-accent-700/40 dark:border-accent-700/40 focus:outline-none focus:border-accent-500 resize-none scrollbar-very-thin transition duration-200`}
        placeholder="Enter job description here"
      />
      <div className={`text-xs w-full text-end font-medium ${getCounterColor()}`}>
        {charCount}/{maxLength}
        {remainingChars <= 100 && <span className="ml-1 text-xs">({remainingChars} remaining)</span>}
      </div>

      {errors.JobDescription?.message && (
        <span className="text-red-500 text-xs -mt-5 block">Job description is required. Please write or copy/paste it</span>
      )}
    </div>
  )
}
